import {
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardForm'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import AppName from 'src/components/AppName/AppName'
import Button from 'src/components/Button/Button'
import ChooseObjectTypeBox from 'src/components/ChooseObjectTypeBox/ChooseObjectTypeBox'
import ForVendorsAndContractorsTooltip from 'src/components/ForVendorsAndContractorsTooltip/ForVendorsAndContractorsTooltip'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import {
  createVirtualCardUrl,
  registerOrganizationUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardsActiveDelegateCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import styled, { css, CSSProp } from 'styled-components'

interface CreateVirtualCardPickContentProps {
  className?: string
  setScreen: (val: CreateVirtualCardScreen) => void
  userHasOrg: boolean
  standardDisabled?: boolean
  recurringDisabled?: boolean
  guestAccessDisabled?: boolean
  hideRecurring?: boolean
}

const ChooseCreateObjectTypeBox = styled(ChooseObjectTypeBox)<{
  orgDisabled?: boolean
}>`
  ${({ orgDisabled }): CSSProp => {
    return orgDisabled
      ? css`
          border: 1px solid ${styleConstants.colors.yellow_dark};
        `
      : css``
  }}

  &:nth-child(n+2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }
`

const CreateVirtualCardPickContent: React.FC<
  CreateVirtualCardPickContentProps
> = (props) => {
  const { analytics } = useAnalyticsContext()
  const hasBudgets =
    useSelector(selectCreditCardsActiveDelegateCountStatisticsFromState) > 0
  const user = useSelector(selectUserListModelFromState)

  const standardCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const recurringCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const recurringGuestCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurringGuest,
  )

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const disabledWithBudgets = !props.userHasOrg && hasBudgets
  const disabledWithoutBudgets = !props.userHasOrg && !hasBudgets

  return (
    <ActionRightFlyoutContent
      className={props.className}
      title={<Trns source={'Create a Virtual Card'} />}
      pickerSubtitle={
        <Trns source={'Choose the type of card you need to create.'} />
      }
    >
      {disabledWithoutBudgets && (
        <SoftAlertBox>
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <div
              css={css`
                margin-top: 20px;
              `}
            >
              <Trns
                source={
                  "Please confirm your org and team to keep using these cards (we've filled things out for you to speed things up)."
                }
              />
            </div>
            <div
              css={css`
                display: flex;
              `}
            >
              <Button
                css={css`
                  margin-top: 20px;
                `}
                linkProps={{
                  to: registerOrganizationUrl(),
                  ariaLabelSource: 'Review and confirm org',
                }}
                design={ButtonDesigns.designCta}
                type={ButtonTypes.button}
                text={<Trns source={'Review and confirm org'} />}
              />
            </div>
          </div>
        </SoftAlertBox>
      )}
      {disabledWithBudgets && (
        <SoftAlertBox
          css={css`
            margin-bottom: 20px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <Trns
              source={
                'You will need to be in an organization in order to create cards. Contact your admin to get invited to their org.'
              }
            />
          </div>
        </SoftAlertBox>
      )}
      <ChooseCreateObjectTypeBox
        imageSrc={standardCardImage}
        title={<Trns source={'Standard Card'} />}
        subtitle={
          <Trns
            source={
              'Create a basic virtual card with all the set controls you need to securely send a company card.'
            }
          />
        }
        onClick={(): void => {
          props.setScreen(CreateVirtualCardScreen.Standard)
          analytics?.track(
            'create virtual card flyout - create standard virtual card - button press',
            {
              interaction: 'button press',
              intent: 'create standard virtual card',
              screen: createVirtualCardUrl(),
            },
          )
        }}
        disabled={props.standardDisabled}
        orgDisabled={!props.userHasOrg}
        id="standardCard"
      />
      {!props.hideRecurring && (
        <ChooseCreateObjectTypeBox
          imageSrc={recurringCardImage}
          title={<Trns source={'Recurring Card'} />}
          subtitle={
            <Trns
              source={
                'Set up a card for recurring expenses at a set frequency, such as monthly or weekly subscriptions.'
              }
            />
          }
          onClick={(): void => {
            props.setScreen(CreateVirtualCardScreen.Recurring)
            analytics?.track(
              'create virtual card flyout - create recurring virtual card - button press',
              {
                interaction: 'button press',
                intent: 'create recurring virtual card',
                screen: createVirtualCardUrl(),
              },
            )
          }}
          disabled={props.recurringDisabled}
          orgDisabled={!props.userHasOrg}
          id="recurringCard"
        />
      )}
      {!showGuestRole && (
        <>
          <div
            css={css`
              display: flex;
              align-items: center;
              margin-top: 40px;
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <Trns source="For vendors and contractors" />
            <ForVendorsAndContractorsTooltip openLeft={false} />
          </div>
          <ChooseCreateObjectTypeBox
            imageSrc={guestCardImage}
            title={<Trns source={'Guest Card'} />}
            subtitle={
              <Trns
                source={
                  'Send virtual cards to people outside of ${appName} via email with a secure link.'
                }
                expressions={{
                  appName: <AppName />,
                }}
              />
            }
            onClick={(): void => {
              props.setScreen(CreateVirtualCardScreen.GuestAccess)
              analytics?.track(
                'create virtual card flyout - create guest access virtual card - button press',
                {
                  interaction: 'button press',
                  intent: 'create guest access virtual card',
                  screen: createVirtualCardUrl(),
                },
              )
            }}
            disabled={props.guestAccessDisabled}
            id="guestAccessCard"
          />
          <ChooseCreateObjectTypeBox
            imageSrc={recurringGuestCardImage}
            title={<Trns source={'Recurring Guest Card'} />}
            subtitle={
              <Trns
                source={
                  'Set up a card for recurring expenses for people outside of ${appName} at a set frequency.'
                }
                expressions={{
                  appName: <AppName />,
                }}
              />
            }
            onClick={(): void => {
              props.setScreen(CreateVirtualCardScreen.RecurringGuest)
              analytics?.track(
                'create virtual card flyout - create recurring guest access virtual card - button press',
                {
                  interaction: 'button press',
                  intent: 'create recurring guest access virtual card',
                  screen: createVirtualCardUrl(),
                },
              )
            }}
            disabled={props.guestAccessDisabled}
            id="recurringGuestAccessCard"
          />
        </>
      )}
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardPickContent
